/* eslint-disable @typescript-eslint/no-base-to-string */
/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters */

/* eslint-disable unused-imports/no-unused-vars */
import { LoggerService } from "../../core";
import { WebinarServiceRB } from "../webinars.service";
import { WebinarDB, WebinarSessionDB, WebinarLogEntryDB } from "./webinarTelemetry.model";

export class WebinarTelemetryDatabase {
    private db: IDBDatabase;

    constructor(private logger: LoggerService, private webinarService: WebinarServiceRB) {}

    public static create(logger: LoggerService, webinarService: WebinarServiceRB): WebinarTelemetryDatabase {
        return new WebinarTelemetryDatabase(logger, webinarService);
    }

    public openDatabase(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open('WebinarDB', 1);

            request.onerror = () => { reject(request.error); };
            request.onsuccess = () => { this.db = request.result; resolve(request.result); };

            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                this.logger.info(`[WebinarTelemetryDatabase] openDatabase ()`);
                this.db = (event.target as IDBOpenDBRequest).result;

                if (!this.db.objectStoreNames.contains('webinars')) {
                    const webinarStore = this.db.createObjectStore('webinars', { keyPath: 'id' });
                }

                if (!this.db.objectStoreNames.contains('sessions')) {
                    const sessionStore = this.db.createObjectStore('sessions', { keyPath: 'id' });
                    sessionStore.createIndex('webinarId', 'webinarId', { unique: false });
                }

                if (!this.db.objectStoreNames.contains('logs')) {
                    const logStore = this.db.createObjectStore('logs', { keyPath: 'id', autoIncrement: true });
                    logStore.createIndex('sessionId', 'sessionId', { unique: false });
                    logStore.createIndex('sid', 'sid', { unique: false });
                    logStore.createIndex('correlatorId', 'correlatorId', { unique: false });
                }
            };
        });
    }

    public async addWebinarToStore(webinarDB: WebinarDB): Promise<void> {
        const { webinarRB, ...prunedWebinarDB } = webinarDB;
        await this.addToStore('webinars', prunedWebinarDB);
    }

    public async getWebinarFromStore(webinarId: string): Promise<WebinarDB | undefined> {
        const result = await this.getFromStore<WebinarDB>('webinars', { key: webinarId });
        return result ? WebinarDB.fromPOJO(result) : undefined;
    }

    public async addSessionToStore(webinarSessionDB: WebinarSessionDB): Promise<void> {
        await this.addToStore<WebinarSessionDB>('sessions', webinarSessionDB);
    }

    public async updateStoreSession(session: WebinarSessionDB): Promise<void> {
        await this.updateInStore('sessions', session.id, session);
    }

    public async addLogEntryToStore(log: WebinarLogEntryDB): Promise<void> {
        delete log.id;
        await this.addToStore<WebinarLogEntryDB>('logs', log);
    }

    public async getLogByCorrelatorId(correlatorId: string): Promise<WebinarLogEntryDB | undefined> {
        const result = await this.getFromStore<WebinarLogEntryDB>('logs', { key: correlatorId, indexName: 'correlatorId' });
        return result ? WebinarLogEntryDB.fromPOJO(result) : undefined;
    }

    public async updateStoreLogEntry(webinarLogEntry: WebinarLogEntryDB): Promise<void> {
        await this.updateInStore('logs', webinarLogEntry.id, webinarLogEntry);
    }

    public async getJSONWebinar(webinarId: string, sessionId?: string): Promise<string> {
        const webinar: any = await this.getFromStore<WebinarDB>('webinars', { key: webinarId }) as WebinarDB[];
        if (sessionId) webinar.sessions = [await this.getFromStore<WebinarSessionDB>('sessions', { key: sessionId }) as WebinarSessionDB[]];
        else webinar.sessions = await this.getFromStore<WebinarSessionDB>('sessions', { key: webinar.id, indexName: 'webinarId', multiple: true }) as WebinarSessionDB[];
        await Promise.all(webinar.sessions.map(async (session: WebinarSessionDB) => {
            session.logs = await this.getFromStore<WebinarLogEntryDB[]>('logs', { key: session.id, indexName: "sessionId", multiple: true }) as WebinarLogEntryDB[];
        }));
        return JSON.stringify(webinar);
    }

    public async getWebinarsAndSessions(): Promise<WebinarDB[]> {
        const webinars = await this.getFromStore<WebinarDB>('webinars') as WebinarDB[];
        await Promise.all(webinars.map(async (webinar) => {
            webinar.active = webinar.id === this.webinarService.conference?.activeWebinar?.id;
            webinar.sessions = await this.getFromStore<WebinarSessionDB>('sessions', { key: webinar.id, indexName: 'webinarId', multiple: true }) as WebinarSessionDB[];
            webinar.sessions.sort((a, b) => a.joinDate - b.joinDate);
        }));
        return webinars;
    }

    public async getSessionAndLogs(sessionId: string): Promise<WebinarSessionDB> {
        const session = await this.getFromStore<WebinarSessionDB>('sessions', { key: sessionId });
        const logs = await this.getFromStore<WebinarLogEntryDB[]>('logs', { key: sessionId, indexName: "sessionId", multiple: true });
        const sessionObj = WebinarSessionDB.fromPOJO(session);
        sessionObj.logs = logs.map(log => WebinarLogEntryDB.fromPOJO(log));
        return sessionObj;
    }

    /* *************************************************************** */

    private addToStore<T extends object>(storeName: string, data: T): Promise<IDBValidKey> {
        return new Promise<IDBValidKey>((resolve, reject) => {
            const tx = this.db.transaction(storeName, "readwrite");
            const request = tx.objectStore(storeName).add(data);
            request.onsuccess = () => { resolve(request.result); };
            request.onerror = () => { reject(request.error); };
            tx.onerror = () => { reject(tx.error); };
            tx.onabort = () => { reject(tx.error); };
        });
    }

    public updateInStore<T>(storeName: string, key: IDBValidKey, value: T): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const tx = this.db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const getRequest = store.get(key);
            getRequest.onerror = () => { reject(getRequest.error); };
            getRequest.onsuccess = () => {
                if (!getRequest.result) { reject(new Error(`Entry with key ${String(key)} not found in ${storeName}`)); return; }
                const putRequest = store.put(value);
                putRequest.onerror = () => { reject(putRequest.error); };
                tx.oncomplete = () => { resolve(); };
                tx.onerror = () => { reject(tx.error); };
                tx.onabort = () => { reject(tx.error); };
            };
        });
    }

    public getFromStoreOld<T>(storeName: string, key?: IDBValidKey, indexName?: string): Promise<T | undefined> {
        return new Promise<T | undefined>((resolve, reject) => {
            const tx = this.db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const index = indexName ? store.index(indexName) : store;
            const request = key ? index.get(key) : index.getAll();
            request.onsuccess = () => { resolve(request.result); };
            request.onerror = () => { reject(request.error); };
            tx.onerror = () => { reject(tx.error); };
            tx.onabort = () => { reject(tx.error); };
        });
    }

    public getFromStore<T>(storeName: string, options?: { key?: IDBValidKey; indexName?: string; multiple?: boolean; }): Promise<T | T[] | undefined> {
        return new Promise((resolve, reject) => {
            const tx = this.db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const source = options?.indexName ? store.index(options.indexName) : store;
            let request: IDBRequest;
            if (options?.multiple) request = options.key ? source.getAll(options.key) : source.getAll();
            else request = options?.key ? source.get(options.key) : source.getAll();
            request.onsuccess = () => { resolve(request.result); };
            request.onerror = () => { reject(request.error); };
            tx.onerror = () => { reject(tx.error); };
            tx.onabort = () => { reject(tx.error); };
        });
    }

    public async clearAllStores(): Promise<void> {
        return new Promise((resolve, reject) => {
            const tx = this.db.transaction(Array.from(this.db.objectStoreNames), "readwrite");
            for (const storeName of this.db.objectStoreNames) { tx.objectStore(storeName).clear(); }
            tx.oncomplete = () => { resolve(); };
            tx.onerror = () => { reject(tx.error); };
        });
    }
}
